import React from 'react';
import { Link } from 'react-router-dom';

type DashboardTile = {
  title: string;
  description: string;
  to: string;
  status: 'Available now' | 'Planned next' | 'Later phase';
};

const dashboardTiles: DashboardTile[] = [
  {
    title: 'My bookings',
    description:
      'Review upcoming appointments, booking history, and status changes.',
    to: '/customer/bookings',
    status: 'Planned next',
  },
  {
    title: 'Saved providers',
    description:
      'Keep a shortlist of preferred providers, salons, and stylists.',
    to: '/customer/saved',
    status: 'Later phase',
  },
  {
    title: 'AI previews',
    description:
      'Access your saved try-on previews and future AI-linked booking flows.',
    to: '/customer/ai-previews',
    status: 'Later phase',
  },
  {
    title: 'Payments',
    description:
      'Track deposits, refunds, and payment history once finance views are added.',
    to: '/customer/payments',
    status: 'Later phase',
  },
  {
    title: 'My profile',
    description:
      'Manage your personal information, preferences, and consent settings.',
    to: '/customer/profile',
    status: 'Planned next',
  },
];

const statusTone: Record<DashboardTile['status'], React.CSSProperties> = {
  'Available now': {
    background: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  'Planned next': {
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
  },
  'Later phase': {
    background: '#f3f4f6',
    color: '#4b5563',
    border: '1px solid #e5e7eb',
  },
};

export default function CustomerDashboardPage() {
  return (
    <main
      style={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f8fafc 0%, #ffffff 45%, #f8fafc 100%)',
        padding: '2rem 1.25rem 4rem',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          display: 'grid',
          gap: '1.5rem',
        }}
      >
        <section
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 24,
            padding: '1.5rem',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.06)',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.45rem 0.8rem',
              borderRadius: 999,
              background: '#e0f2fe',
              color: '#075985',
              fontSize: '0.82rem',
              fontWeight: 700,
              marginBottom: '1rem',
            }}
          >
            Customer area
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              color: '#0f172a',
            }}
          >
            Customer dashboard
          </h1>

          <p
            style={{
              marginTop: '0.9rem',
              maxWidth: 760,
              color: '#475569',
              lineHeight: 1.75,
              fontSize: '1rem',
            }}
          >
            This should be the customer-facing post-login home, separate from
            provider-owner operations and separate again from HnB staff admin
            tools. Use it as the entry point for bookings, profile, payments,
            saved providers, and future AI usage history.
          </p>

          <div
            style={{
              marginTop: '1.25rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <Link
              to="/find-provider"
              style={{
                textDecoration: 'none',
                borderRadius: 999,
                padding: '0.9rem 1.1rem',
                background: '#111827',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              Find providers
            </Link>

            <Link
              to="/customer/bookings"
              style={{
                textDecoration: 'none',
                borderRadius: 999,
                padding: '0.9rem 1.1rem',
                background: '#ffffff',
                color: '#111827',
                fontWeight: 700,
                fontSize: '0.95rem',
                border: '1px solid #d1d5db',
              }}
            >
              View bookings
            </Link>
          </div>
        </section>

        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1rem',
          }}
        >
          {dashboardTiles.map((tile) => (
            <article
              key={tile.title}
              style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 20,
                padding: '1.25rem',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.05)',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.35rem 0.65rem',
                  borderRadius: 999,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  marginBottom: '0.9rem',
                  ...statusTone[tile.status],
                }}
              >
                {tile.status}
              </div>

              <h2
                style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                }}
              >
                {tile.title}
              </h2>

              <p
                style={{
                  marginTop: '0.7rem',
                  marginBottom: '1rem',
                  color: '#475569',
                  lineHeight: 1.7,
                  fontSize: '0.94rem',
                }}
              >
                {tile.description}
              </p>

              <Link
                to={tile.to}
                style={{
                  textDecoration: 'none',
                  color: '#111827',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                }}
              >
                Open area →
              </Link>
            </article>
          ))}
        </section>

        <section
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 24,
            padding: '1.5rem',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '1.2rem',
              color: '#0f172a',
              letterSpacing: '-0.02em',
            }}
          >
            Surface guidance
          </h2>

          <div
            style={{
              marginTop: '1rem',
              display: 'grid',
              gap: '0.85rem',
              color: '#334155',
              fontSize: '0.95rem',
              lineHeight: 1.7,
            }}
          >
            <div>
              • Customers should never land inside the HnB staff admin shell after login.
            </div>
            <div>
              • Provider owners should continue to use the onboarding gate and provider dashboard.
            </div>
            <div>
              • HnB staff should stay isolated inside the dedicated staff dashboard route tree.
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}